import { useState, useEffect } from 'react';
import {
  TrendingUp,
  ArrowUpRight,
  Layers,
  Clock,
  XCircle,
  Gauge,
  AlertTriangle,
  CheckCircle2,
} from 'lucide-react';
import {
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
  Area,
  AreaChart,
  PieChart,
  Pie,
  Cell,
} from 'recharts';
import api from '../utils/api';

// Local YYYY-MM-DD (avoids the UTC/local off-by-one that mislabels chart days)
const localYMD = (d: Date): string => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

// ---------- KPI card with sparkline + trend chip ----------
function StatCard({ title, value, icon: Icon, hex, spark, dataKey, todayDelta, loading }: any) {
  const gid = `spark-${dataKey}`;
  return (
    <div className="nkpi group !p-0 overflow-hidden relative">
      <div
        className="absolute -top-10 -right-10 w-28 h-28 rounded-full opacity-[0.07] blur-2xl transition-opacity group-hover:opacity-[0.12]"
        style={{ backgroundColor: hex }}
      />
      <div className="relative z-10 p-5 pb-0">
        <div className="flex items-start justify-between">
          <div
            className="w-11 h-11 rounded-2xl flex items-center justify-center shadow-sm"
            style={{ backgroundColor: `${hex}14`, color: hex }}
          >
            <Icon className="w-5 h-5" />
          </div>
          {todayDelta > 0 && (
            <div
              className="flex items-center gap-1 px-2 py-1 rounded-full text-[10px] font-black"
              style={{ backgroundColor: `${hex}14`, color: hex }}
            >
              <ArrowUpRight className="w-3 h-3" />
              {todayDelta} today
            </div>
          )}
        </div>
        <h3 className="mt-4 text-[34px] leading-none font-extrabold text-slate-900 tracking-tight">
          {loading ? '—' : value}
        </h3>
        <p className="mt-1.5 section-eyebrow">{title}</p>
      </div>
      {/* sparkline */}
      <div className="h-12 mt-2 -mb-px">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={spark} margin={{ top: 4, right: 0, left: 0, bottom: 0 }}>
            <defs>
              <linearGradient id={gid} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={hex} stopOpacity={0.35} />
                <stop offset="100%" stopColor={hex} stopOpacity={0} />
              </linearGradient>
            </defs>
            <Area type="monotone" dataKey={dataKey} stroke={hex} strokeWidth={2}
              fill={`url(#${gid})`} isAnimationActive={false} dot={false} />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

export default function Dashboard() {
  const [firstName, setFirstName] = useState('Hospital Admin');
  const [stats, setStats] = useState({
    total: 0, approved: 0, pending: 0, rejected: 0, missing_info: 0,
    ocr_processed: 0, uploaded: 0, pendingToday: 0,
  });
  const [chartData, setChartData] = useState<any[]>([]);
  const [today, setToday] = useState<any>({ total: 0, pending: 0, approved: 0, rejected: 0 });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const fetchData = async () => {
      setLoading(true); setError(false);
      try {
        const [userRes, statsRes, invoicesRes] = await Promise.all([
          api.get('/auth/users/me'),
          api.get('/invoices/stats'),
          api.get('/invoices/'),
        ]);
        if (cancelled) return;
        if (userRes.data.first_name) setFirstName(userRes.data.first_name);

        const dayLabels = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
        const days = Array.from({ length: 7 }, (_, i) => {
          const d = new Date();
          d.setDate(d.getDate() - (6 - i));
          return { key: localYMD(d), name: dayLabels[d.getDay()] };
        });
        const todayKey = days[6].key;
        let pendingTodayCount = 0;

        const aggregated = days.map(({ key, name }) => {
          let dayTotal = 0, dayPending = 0, dayApproved = 0, dayRejected = 0;
          invoicesRes.data.forEach((inv: any) => {
            if (!inv.created_at) return;
            if (localYMD(new Date(inv.created_at)) !== key) return;
            inv.line_items.forEach((item: any) => {
              dayTotal++;
              if (item.status === 'Approved') dayApproved++;
              else if (item.status === 'Rejected') dayRejected++;
              else { dayPending++; if (key === todayKey) pendingTodayCount++; }
            });
          });
          return { name, total: dayTotal, pending: dayPending, approved: dayApproved, rejected: dayRejected };
        });

        if (statsRes.data) setStats({ ...statsRes.data, uploaded: statsRes.data.total, pendingToday: pendingTodayCount });
        setChartData(aggregated);
        setToday(aggregated[aggregated.length - 1] || { total: 0, pending: 0, approved: 0, rejected: 0 });
      } catch (err) {
        if (!cancelled) setError(true);
        console.error('Failed to fetch dashboard data');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchData();
    return () => { cancelled = true; };
  }, []);

  const approvalRate = stats.total > 0 ? Math.round((stats.approved / stats.total) * 100) : 0;

  const donut = [
    { name: 'Approved', value: stats.approved, color: '#10B981' },
    { name: 'Pending', value: stats.pending, color: '#F59E0B' },
    { name: 'Rejected', value: stats.rejected, color: '#F43F5E' },
    { name: 'Incomplete', value: stats.missing_info, color: '#01A5D6' },
  ].filter(d => d.value > 0);

  return (
    <div className="relative max-w-[1600px] mx-auto space-y-6 animate-in fade-in duration-700 pb-10 pt-4">
      {/* Greeting */}
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
        <div className="flex flex-col gap-1">
          <h1 className="text-[22px] font-semibold text-[#0A0A0A] tracking-tight leading-tight">
            Hello, <span className="font-bold">{firstName}</span>
          </h1>
          <p className="section-eyebrow">{stats.pendingToday} claims awaiting review today</p>
        </div>
        <div className="flex items-center gap-2 px-3.5 py-2 rounded-full bg-white border border-slate-200/70 shadow-sm">
          <span className="relative flex h-2 w-2">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
            <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500" />
          </span>
          <span className="text-[11px] font-black uppercase tracking-wide text-slate-500">Live</span>
        </div>
      </div>

      {error && (
        <div className="ncard border-rose-200/70 !bg-rose-50 p-4 flex items-center gap-3">
          <AlertTriangle className="w-5 h-5 text-rose-500 flex-none" />
          <p className="text-[13px] font-semibold text-rose-700">
            Couldn't load dashboard data. Showing last known values — refresh to retry.
          </p>
        </div>
      )}

      {/* KPI cards with sparklines */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
        <StatCard title="Total Claims" value={stats.uploaded} icon={Layers} hex="#1C4E9C"
          spark={chartData} dataKey="total" todayDelta={today.total} loading={loading} />
        <StatCard title="Pending Review" value={stats.pending} icon={Clock} hex="#F59E0B"
          spark={chartData} dataKey="pending" todayDelta={today.pending} loading={loading} />
        <StatCard title="Approved" value={stats.approved} icon={CheckCircle2} hex="#10B981"
          spark={chartData} dataKey="approved" todayDelta={today.approved} loading={loading} />
        <StatCard title="Rejected" value={stats.rejected} icon={XCircle} hex="#F43F5E"
          spark={chartData} dataKey="rejected" todayDelta={today.rejected} loading={loading} />
      </div>

      {/* Donut + reviewed chart */}
      <div className="grid grid-cols-1 xl:grid-cols-12 gap-6 items-stretch">
        {/* Donut */}
        <div className="xl:col-span-4 ncard p-6 flex flex-col">
          <h3 className="section-eyebrow mb-4">Status distribution</h3>
          <div className="relative flex-1 min-h-[200px]">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={donut.length ? donut : [{ name: 'None', value: 1, color: '#E2E8F0' }]}
                  dataKey="value" cx="50%" cy="50%" innerRadius={62} outerRadius={88}
                  paddingAngle={donut.length > 1 ? 3 : 0} stroke="none" isAnimationActive={false}>
                  {(donut.length ? donut : [{ color: '#E2E8F0' }]).map((d: any, i: number) => (
                    <Cell key={i} fill={d.color} />
                  ))}
                </Pie>
                <Tooltip
                  content={({ active, payload }: any) =>
                    active && payload?.length ? (
                      <div className="bg-[#1C4E9C] text-white px-3 py-1.5 rounded-lg text-[11px] font-bold shadow-xl">
                        {payload[0].name}: {payload[0].value}
                      </div>
                    ) : null
                  }
                />
              </PieChart>
            </ResponsiveContainer>
            {/* Center label */}
            <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
              <span className="text-[32px] font-extrabold text-slate-900 leading-none">{approvalRate}%</span>
              <span className="section-eyebrow mt-1">Approved</span>
            </div>
          </div>
          {/* Legend */}
          <div className="grid grid-cols-2 gap-2 mt-4">
            {[
              { label: 'Approved', value: stats.approved, color: '#10B981' },
              { label: 'Pending', value: stats.pending, color: '#F59E0B' },
              { label: 'Rejected', value: stats.rejected, color: '#F43F5E' },
              { label: 'Incomplete', value: stats.missing_info, color: '#01A5D6' },
            ].map((l) => (
              <div key={l.label} className="flex items-center gap-2">
                <span className="w-2.5 h-2.5 rounded-full flex-none" style={{ backgroundColor: l.color }} />
                <span className="text-[11px] font-bold text-slate-500 flex-1 truncate">{l.label}</span>
                <span className="text-[12px] font-black text-slate-800">{l.value}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Reviewed bar chart */}
        <div className="xl:col-span-8 ncard p-6">
          <div className="flex items-center justify-between mb-6">
            <h3 className="section-eyebrow">
              Claims reviewed <span className="text-slate-300 ml-1 normal-case tracking-normal font-bold">(Last 7 days)</span>
            </h3>
            <div className="flex items-center gap-4">
              {[['Total', '#1C4E9C'], ['Pending', '#F59E0B'], ['Approved', '#10B981'], ['Rejected', '#F43F5E']].map(([l, c]) => (
                <div key={l} className="flex items-center gap-1.5">
                  <span className="w-2 h-2 rounded-full" style={{ backgroundColor: c as string }} />
                  <span className="text-[9px] font-black uppercase tracking-wide text-slate-400">{l}</span>
                </div>
              ))}
            </div>
          </div>
          <div className="h-[250px] w-full">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData} margin={{ top: 10, right: 10, left: -20, bottom: 0 }} barGap={2} barCategoryGap="22%">
                <defs>
                  {[['gTotal', '#1C4E9C'], ['gPending', '#F59E0B'], ['gApproved', '#10B981'], ['gRejected', '#F43F5E']].map(([id, c]) => (
                    <linearGradient key={id} id={id} x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor={c} stopOpacity={0.95} />
                      <stop offset="100%" stopColor={c} stopOpacity={0.55} />
                    </linearGradient>
                  ))}
                </defs>
                <CartesianGrid strokeDasharray="4 6" vertical={false} stroke="#e2e8f0" />
                <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fill: '#64748b', fontSize: 11, fontWeight: 600 }} dy={10} />
                <YAxis axisLine={false} tickLine={false} tick={{ fill: '#94a3b8', fontSize: 10 }} />
                <Tooltip
                  cursor={{ fill: '#1C4E9C', fillOpacity: 0.04, radius: 8 }}
                  content={({ active, payload, label }: any) =>
                    active && payload?.length ? (
                      <div className="bg-[#1C4E9C] text-white p-3 rounded-xl shadow-xl min-w-[140px]">
                        <p className="text-[10px] font-black mb-2 opacity-70 uppercase tracking-widest border-b border-white/10 pb-1">{label}</p>
                        <div className="space-y-1.5">
                          {payload.map((item: any, idx: number) => (
                            <div key={idx} className="flex items-center justify-between gap-4">
                              <span className="text-[10px] font-bold uppercase opacity-80">{item.name}:</span>
                              <span className="text-xs font-black">{item.value}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    ) : null
                  }
                />
                <Bar name="Total" dataKey="total" fill="url(#gTotal)" radius={[5, 5, 0, 0]} maxBarSize={12} />
                <Bar name="Pending" dataKey="pending" fill="url(#gPending)" radius={[5, 5, 0, 0]} maxBarSize={12} />
                <Bar name="Approved" dataKey="approved" fill="url(#gApproved)" radius={[5, 5, 0, 0]} maxBarSize={12} />
                <Bar name="Rejected" dataKey="rejected" fill="url(#gRejected)" radius={[5, 5, 0, 0]} maxBarSize={12} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Pipeline band */}
      <div className="ncard p-6">
        <h3 className="section-eyebrow mb-5">Claims pipeline</h3>
        <div className="flex items-center gap-3 overflow-x-auto no-scrollbar">
          {[
            { label: 'Total', count: stats.uploaded, hex: '#1C4E9C', icon: Layers },
            { label: 'Pending', count: stats.pending, hex: '#F59E0B', icon: Clock },
            { label: 'Approved', count: stats.approved, hex: '#10B981', icon: CheckCircle2 },
            { label: 'Rejected', count: stats.rejected, hex: '#F43F5E', icon: XCircle },
          ].map((s, i, arr) => (
            <div key={s.label} className="flex items-center gap-3 flex-1 min-w-[150px]">
              <div className="flex-1 rounded-2xl border border-slate-200/70 p-4 flex items-center gap-3"
                style={{ background: `linear-gradient(135deg, ${s.hex}0D, transparent)` }}>
                <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-none"
                  style={{ backgroundColor: `${s.hex}1A`, color: s.hex }}>
                  <s.icon className="w-5 h-5" />
                </div>
                <div>
                  <div className="text-2xl font-extrabold text-slate-900 leading-none">{s.count}</div>
                  <div className="text-[10px] font-bold uppercase tracking-wide text-slate-400 mt-1">{s.label}</div>
                </div>
              </div>
              {i < arr.length - 1 && <TrendingUp className="w-4 h-4 text-slate-300 rotate-45 flex-none hidden sm:block" />}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
